/**
 * Returns true if a student is currently a minor (under 18) given their
 * birth_date (YYYY-MM-DD). Empty / invalid date → assume adult (false).
 */
export function isMinor(birthDate) {
  if (!birthDate) return false;
  const b = new Date(birthDate);
  if (Number.isNaN(b.getTime())) return false;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age < 18;
}

/**
 * Devuelve las facturas del alumno normalizadas — mezcla el array moderno
 * `invoices` con el campo legacy `invoice` (singleton). Descarta stubs vacíos.
 */
export function studentInvoices(student) {
  const arr = Array.isArray(student?.invoices) ? student.invoices : [];
  const hasData = (i) =>
    i && (i.number || i.date || i.classes_included || i.emitted || i.notes);
  const cleaned = arr.filter(hasData);
  if (cleaned.length) return cleaned;
  const legacy = student?.invoice;
  return legacy && hasData(legacy) ? [legacy] : [];
}

/**
 * Computes documentation status of a student.
 * Returns { complete, missing, invoicePending, invoiceEmittedCount, invoiceTotal, invoices }.
 * Documents flagged `only_minor` are ignored when the student is adult.
 *
 * Regla de "Factura" (V1.21):
 *   - invoiceEmittedCount = número de facturas con `emitted=true` y `number` no vacío.
 *   - invoiceTotal        = número total de facturas registradas (emitidas o no).
 *   - invoicePending      = tiene facturas registradas pero ninguna emitida.
 *   - "Factura emitida" se añade a `missing` únicamente si NO hay ninguna factura emitida.
 */
export function computeDocsStatus(student) {
  const docs = student?.documents || [];
  const minor = isMinor(student?.birth_date);
  const applicable = docs.filter((d) => !d.only_minor || minor);
  const missing = applicable.filter((d) => !d.delivered).map((d) => d.name);

  const invoices = studentInvoices(student);
  const emittedInvoices = invoices.filter(
    (i) => i?.emitted && String(i?.number || "").trim()
  );
  const invoiceEmittedCount = emittedInvoices.length;
  const invoiceTotal = invoices.length;
  const hasEmitted = invoiceEmittedCount > 0;
  const invoicePending = invoiceTotal > 0 && !hasEmitted;
  if (!hasEmitted) missing.push("Factura emitida");
  return {
    complete: missing.length === 0,
    missing,
    invoicePending,
    invoiceEmittedCount,
    invoiceTotal,
    invoiceCount: invoiceEmittedCount, // legacy alias (kept for retro-compat)
    invoices,
  };
}
